/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteDashboardShell, { useSiteDashboard } from "@/components/customer/SiteDashboardShell";
import { fetchSiteOrders, createSiteOrder, fetchSiteProducts } from "@/lib/api";

function money(minor) { return `₱${((minor || 0) / 100).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`; }

const FILTERS = [
  ["", "All"], ["new", "New"], ["processing", "Needs Processing"], ["ready_to_ship", "Ready to Ship"],
  ["shipped", "Shipped"], ["delivered", "Completed"], ["cancelled", "Cancelled"],
];

function CreateOrderForm({ token, slug, onCreated }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [error, setError] = useState("");

  useEffect(() => { if (open && !products) fetchSiteProducts(token, slug, { status: "active" }).then((r) => setProducts(r.items)).catch(() => {}); }, [open, products, token, slug]);

  if (!open) return <button onClick={() => setOpen(true)} className="mb-5 rounded-lg bg-[#0F172A] px-4 py-2 text-sm font-medium text-white">Create Order</button>;
  return (
    <div className="mb-5 rounded-2xl border border-[#E2E8F0] bg-white p-5">
      <p className="text-xs text-[#94A3B8] mb-3">For orders taken by phone, Messenger, or in person. Orders placed by shoppers on your website's own store appear here automatically.</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <input placeholder="Customer name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
        <select value={productId} onChange={(e) => setProductId(e.target.value)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white">
          <option value="">Select product…</option>
          {(products || []).map((p) => <option key={p.id} value={p.id}>{p.name} ({money(p.price_minor)}, stock {p.stock_quantity})</option>)}
        </select>
        <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <div className="mt-3 flex gap-2">
        <button disabled={!customerName || !productId} onClick={() => {
          setError("");
          createSiteOrder(token, slug, { customer_name: customerName, items: [{ product_id: productId, quantity: Number(quantity) }] })
            .then(() => { setCustomerName(""); setProductId(""); setQuantity("1"); setOpen(false); onCreated(); })
            .catch((err) => setError(err.response?.data?.detail || "Could not create order."));
        }} className="rounded-lg bg-[#0F172A] px-4 py-2 text-sm font-medium text-white disabled:opacity-40">Save</button>
        <button onClick={() => setOpen(false)} className="rounded-lg border border-[#E2E8F0] px-4 py-2 text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function SiteOrders() {
  const dashboard = useSiteDashboard();
  const { token, slug } = dashboard;
  const [filter, setFilter] = useState("");
  const [orders, setOrders] = useState(null);

  const load = () => {
    if (!token) return;
    fetchSiteOrders(token, slug, filter ? { fulfillment_status: filter } : {}).then(setOrders).catch(() => {});
  };
  useEffect(load, [token, slug, filter]);

  return (
    <SiteDashboardShell active="orders" dashboard={dashboard}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="text-lg font-bold font-display text-[#0F172A]">Orders</h2>
        <a href={`/store/${slug}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#10B981] hover:underline">View your storefront ↗</a>
      </div>
      <CreateOrderForm token={token} slug={slug} onCreated={load} />

      <div className="flex gap-2 mb-4 flex-wrap">
        {FILTERS.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} className={`rounded-lg px-3 py-1.5 text-sm font-medium ${filter === v ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569]"}`}>{label}</button>
        ))}
      </div>

      <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden overflow-x-auto">
        {!orders ? (
          <p className="p-6 text-sm text-[#475569]">Loading…</p>
        ) : orders.items.length === 0 ? (
          <p className="p-6 text-sm text-[#94A3B8]">No orders yet. Orders generated through your connected store will appear here.</p>
        ) : (
          <table className="w-full min-w-[600px] text-left text-sm">
            <thead className="bg-[#F8FAFC] text-xs uppercase tracking-wide text-[#94A3B8]">
              <tr><th className="px-4 py-2.5">Order</th><th className="px-4 py-2.5">Customer</th><th className="px-4 py-2.5">Total</th><th className="px-4 py-2.5">Payment</th><th className="px-4 py-2.5">Fulfillment</th></tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {orders.items.map((o) => (
                <tr key={o.id} className="hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3"><Link to={`/account/websites/${slug}/orders/${o.id}`} className="font-medium text-[#10B981] hover:underline">{o.order_number}</Link></td>
                  <td className="px-4 py-3 text-[#0F172A]">{o.customer_name}</td>
                  <td className="px-4 py-3">{money(o.total_minor)}</td>
                  <td className="px-4 py-3 capitalize">{o.payment_status}</td>
                  <td className="px-4 py-3 capitalize">{o.fulfillment_status.replace(/_/g, " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </SiteDashboardShell>
  );
}
